/**
 * FDR Audit Trail — Immutable audit log for all FDR oversight actions.
 */
import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL;

const EVENT_LABELS = {
  entity_created: 'Entity Created',
  entity_updated: 'Entity Updated',
  contact_created: 'Contact Added',
  contact_updated: 'Contact Updated',
  contact_deactivated: 'Contact Removed',
  contract_created: 'Contract Created',
  contract_updated: 'Contract Updated',
  function_delegated: 'Function Delegated',
  function_updated: 'Function Updated',
  assessment_created: 'Assessment Scheduled',
  assessment_updated: 'Assessment Updated',
  assessment_responses_submitted: 'Responses Submitted',
  assessment_completed: 'Assessment Completed',
  finding_created: 'Finding Created',
  finding_updated: 'Finding Updated',
  corrective_action_created: 'CAP Created',
  corrective_action_updated: 'CAP Updated',
  attestation_created: 'Attestation Created',
  attestation_updated: 'Attestation Updated',
  screening_initiated: 'Screening Started',
  exclusion_screening_completed: 'Screening Completed',
  exclusion_resolved: 'Match Resolved',
  activity_logged: 'Activity Logged',
};

const EVENT_COLORS = {
  entity_created: 'bg-blue-500',
  contract_created: 'bg-indigo-500',
  assessment_completed: 'bg-emerald-500',
  finding_created: 'bg-orange-500',
  corrective_action_created: 'bg-amber-500',
  exclusion_screening_completed: 'bg-amber-500',
  exclusion_resolved: 'bg-orange-500',
};

export default function FDRAuditTrail() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/fdr/audit`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setEvents(data.events || []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Audit Trail</h2>
        <p className="text-sm text-slate-500 mt-0.5">Immutable log of all FDR oversight actions — SOC 2 / CMS audit evidence</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading...</div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No audit events yet</div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-[700px] overflow-y-auto">
            {events.map(e => {
              const dotColor = EVENT_COLORS[e.event_type] || 'bg-slate-400';
              return (
                <div key={e.id} className="px-6 py-3 hover:bg-slate-50/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${dotColor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 text-sm">
                          {EVENT_LABELS[e.event_type] || e.event_type}
                        </span>
                        {e.entity_type && (
                          <span className="text-xs text-slate-400">{e.entity_type.replace('fdr_', '')}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                        <span>{formatDate(e.created_at)}</span>
                        {e.actor_name && <span>by {e.actor_name}</span>}
                        {e.metadata && typeof e.metadata === 'object' && e.metadata.fields_updated && (
                          <span>Fields: {e.metadata.fields_updated.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
